import { moneefloApi } from "service/moneeflo/client";
import { APPLICATIONS_LIMIT } from "./constants";
import { toBusinessError } from "./errors";
import type {
  BusinessDetail,
  BusinessListItem,
  CreateBusinessInput,
  CreateBusinessResult,
  ResendInviteResult,
} from "./types";

const CREATE_TIMEOUT_MS = 30_000;

/** The calls to core. Only ./index.ts imports this. */
async function call<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    return (await request).data;
  } catch (error) {
    throw toBusinessError(error);
  }
}

export const live = {
  create: (input: CreateBusinessInput) =>
    // Two paid checks run inside this call, so it may take a while; the shared
    // client stays as it is and only this request is bounded.
    call<CreateBusinessResult>(
      moneefloApi.post("/ops/businesses", input, { timeout: CREATE_TIMEOUT_MS }),
    ),

  /**
   * Core applies `state` after taking the newest rows, so the screens ask for
   * the most it gives and filter here.
   */
  async list(): Promise<BusinessListItem[]> {
    const data = await call<{ businesses: BusinessListItem[] }>(
      moneefloApi.get("/ops/businesses", { params: { limit: APPLICATIONS_LIMIT } }),
    );
    return data.businesses;
  },

  detail: (id: string) =>
    call<BusinessDetail>(moneefloApi.get(`/ops/businesses/${id}`)),

  resendInvite: (id: string) =>
    call<ResendInviteResult>(moneefloApi.post(`/ops/businesses/${id}/invite/resend`)),

  approveItem: (itemId: string) =>
    call<{ id: string; status: "approved" }>(
      moneefloApi.post(`/ops/review-items/${itemId}/approve`),
    ),

  holdItem: (itemId: string, reasonCode: string, reasonNote: string) =>
    call<{ id: string; status: "held" }>(
      moneefloApi.post(`/ops/review-items/${itemId}/hold`, {
        reasonCode,
        reasonNote,
      }),
    ),

  approve: (id: string) =>
    call<{ id: string; state: "registered" }>(
      moneefloApi.post(`/ops/businesses/${id}/approve`),
    ),

  notApprove: (id: string, reason: string) =>
    call<unknown>(moneefloApi.post(`/ops/businesses/${id}/not-approve`, { reason })),
};
